/**
 * Responsibility: Implement topics behavior for the ui surface.
 * Scope: Owns ui concerns only; cross-surface orchestration is excluded.
 */
// ============================================================
// UI/TOPICS.TS — Typed dispatcher topic:verb constants
//
// Kept centralized for discoverability and rename-refactoring.
// Modules own their *data shapes* (types.ts); this file owns
// the event surface so the whole system is auditable in one place.
//
// Usage:
//   dispatcher.trigger('game-store', TOPICS.game.actionComplete, data)
//   dispatcher.on(TOPICS.game.actionComplete, (event, data) => ...)
// ============================================================

export const TOPICS = {

  auth: {
    signedIn:   'auth:signedIn',    // data: { user: User }
    signedOut:  'auth:signedOut',
  },

  game: {
    started:          'game:started',         // data: GameState (new game)
    loaded:           'game:loaded',          // data: GameState (from save)
    saved:            'game:saved',           // data: { saveId: string }
    actionStart:      'game:actionStart',     // data: { input: string, actionId?: number }
    actionUpdate:     'game:actionUpdate',    // data: GameState (partial update, e.g. streaming prose)
    actionComplete:   'game:actionComplete',  // data: ActionResult + { actionId?: number, isCompanionAction?: boolean }
    actionFailed:     'game:actionFailed',    // data: { error: string, actionId?: number }
    roomChanged:      'game:roomChanged',     // data: Room
    inventoryChanged: 'game:inventoryChanged',
    vitalsChanged:    'game:vitalsChanged',   // data: Vitals
    imageReady:       'game:imageReady',      // data: { url: string, kind: 'vision'|'portrait' }
    entropyShift:     'game:entropyShift',    // data: TraitShiftAIResponse
  },

  combat: {
    started:    'combat:started',   // data: { npc: NPC }
    turnStart:  'combat:turnStart', // data: { round: number }
    turnEnd:    'combat:turnEnd',   // data: { round: number, actor: 'player'|'npc'|'companion', actorName?: string, action?: string, outcome: string, vitalityDelta?: number, spiritDelta?: number, essenceDelta?: number }
    npcTurnStart: 'combat:npcTurnStart', // data: { npc: NPC }
    targetChanged: 'combat:targetChanged', // data: { targetName: string, foes: CombatFoeState[] }
    ended:      'combat:ended',     // data: { victory: boolean }
    fled:       'combat:fled',
    closeRequested: 'combat:closeRequested',
  },

  ui: {
    dialogOpen:    'ui:dialogOpen',    // data: GameDialogOptions ({ content, data, options })
    dialogClose:   'ui:dialogClose',
    diceRollRequired: 'ui:diceRollRequired', // data: { title: string, dc: number, bonus: number, primaryStat: string, chance: number, bonusSummary?: string, bonusDetail?: string }
    diceRolled:      'ui:diceRolled',      // data: { roll: number }
    talkPromptOpen: 'ui:talkPromptOpen', // data: { targetName: string, baseInput: string }
    sidebarOpen:   'ui:sidebarOpen',
    sidebarClose:  'ui:sidebarClose',
    tabChange:     'ui:tabChange',     // data: { tab: string }
    notify:        'ui:notify',        // data: { message: string, level: 'info'|'warn'|'error' }
    openItem:      'ui:openItem',      // data: { name: string, location?: string }
    openSettings:  'ui:openSettings',
    openSystemControls: 'ui:openSystemControls',
    aiConfigSaved: 'ui:aiConfigSaved', // data: AIProviderConfig
    playerProfileOpen: 'ui:playerProfileOpen',
    tutorialStart: 'ui:tutorialStart', // no data — overlay reads localStorage itself
    howToPlayOpen: 'ui:howToPlayOpen', // no data — open the how-to-play modal
    overlayOpened: 'ui:overlayOpened', // data: { key: string }
    overlayClosed: 'ui:overlayClosed', // data: { key: string }
    diceRollCloseRequested: 'ui:diceRollCloseRequested',
    sysGalleryCloseRequested: 'ui:sysGalleryCloseRequested',
    playerDamaged: 'ui:playerDamaged', // data: { delta: number }
  },

  trade: {
    open:     'trade:open',     // data: { npc: NPC }
    close:    'trade:close',
    proposed: 'trade:proposed', // data: { offer: string[] }
  },

  companion: {
    chatOpen:    'companion:chatOpen',    // data: { companion: Companion }
    chatClose:   'companion:chatClose',
    chatUpdated: 'companion:chatUpdated', // data: { companionName: string, conversations: ConversationEntry[], phase?: 'player'|'companion'|'error', error?: string }
  },

  action: {
    initiated: 'action:initiated', // data: ActionInitiatedEvent (action, context, callback)
  },

  ai: {
    thinking: 'ai:thinking', // data: { job: JobType }
    done:     'ai:done',     // data: { job: JobType }
    error:    'ai:error',    // data: { job: JobType, message: string }
  },

  world: {
    ticked:       'world:ticked',       // data: WorldEvent[] (new events from tick)
    enriched:     'world:enriched',     // data: { narrative: string }
    npcMoved:     'world:npcMoved',     // data: { id: string, location: string }
    questUpdated: 'world:questUpdated', // data: { id: string, newStatus: QuestStatus }
  },

  supervisor: {
    runStarted:  'supervisor:runStarted',  // data: { runId: string, reason: string }
    runEnded:    'supervisor:runEnded',    // data: { runId: string, reason: string, durationMs: number, incidents: number, repaired: number, aborted: boolean }
    degraded:    'supervisor:degraded',    // data: { reason: string }
  },

  system: {
    fatalError:      'system:fatalError',      // data: { message: string, error?: Error }
    feedbackRequest: 'system:feedbackRequest', // no data
    pwaInstallable:  'system:pwaInstallable',
    pwaInstalled:    'system:pwaInstalled',
    pwaUpdateReady:  'system:pwaUpdateReady',
  },

} as const
// Flat union of all topic:verb strings — useful for handler types
type TopicGroup = typeof TOPICS[keyof typeof TOPICS]
export type TopicString = TopicGroup[keyof TopicGroup]
